/**
 * Auth utilities for the K-Quest frontend.
 *
 * Flow:
 *   1. User clicks "Sign in with Google" -> goes to /api/auth/google/start
 *   2. Backend bounces to Google, then back to /api/auth/google/callback
 *   3. Backend redirects user to <SITE>/#session_token=...&user_id=...
 *   4. parseAuthCallback() reads the hash, persists, then strips the hash
 *   5. fetchMe() validates the session and returns the profile
 */

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8787';
const SESSION_KEY = 'kq_session_token';
const USER_KEY    = 'kq_user_cache';

export interface KQUser {
  id: string;
  username: string;
  youtube_channel_title?: string;
  avatar_url?: string;
  credits_cents: number;
  created_at: string;
  tier?: 'apprendista' | 'cavaliere' | 're' | null;
  tier_until?: string | null;
  tier_active?: boolean;
  tier_votes_remaining?: number;
  tier_quests_remaining?: number;
  tier_veto_remaining?: number;
  paid_until?: string | null;
  total_donated_cents?: number;
}

export function getSessionToken(): string | null {
  return localStorage.getItem(SESSION_KEY);
}

export function setSessionToken(t: string) {
  localStorage.setItem(SESSION_KEY, t);
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getCachedUser(): KQUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as KQUser; } catch { return null; }
}

/**
 * Build the Google sign-in URL with a return-to that bounces back to the
 * exact page the user is on now.
 */
export function googleSignInUrl(): string {
  const returnTo = window.location.origin + window.location.pathname;
  const params = new URLSearchParams({ return_to: returnTo });
  return `${API_BASE}/api/auth/google/start?${params.toString()}`;
}

/**
 * Call after page load to capture a fresh session arriving via #hash.
 * Returns true if a new session was picked up.
 */
export function parseAuthCallback(): boolean {
  const hash = window.location.hash.slice(1);
  if (!hash) return false;
  const params = new URLSearchParams(hash);
  const tok = params.get('session_token');
  const userId = params.get('user_id');
  if (!tok || !userId) return false;

  setSessionToken(tok);
  // Optimistic cache from the params
  const cached: KQUser = {
    id: userId,
    username: params.get('username') ?? '—',
    youtube_channel_title: params.get('youtube_channel_title') || undefined,
    avatar_url: params.get('avatar_url') || undefined,
    credits_cents: 0,
    created_at: new Date().toISOString(),
  };
  localStorage.setItem(USER_KEY, JSON.stringify(cached));

  // Strip the hash so it doesn't stay in the URL bar
  history.replaceState(null, '', window.location.pathname + window.location.search);
  return true;
}

/** Fetch /me and cache the result. */
export async function fetchMe(): Promise<KQUser | null> {
  const tok = getSessionToken();
  if (!tok) return null;
  try {
    const r = await fetch(`${API_BASE}/api/auth/google/me`, {
      headers: { Authorization: `Bearer ${tok}` },
    });
    if (!r.ok) {
      if (r.status === 401) clearSession();
      return null;
    }
    const data = await r.json() as { user: KQUser };
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    return data.user;
  } catch {
    return null;
  }
}
